import { Table, TableBody, TableHeader, TableRow } from '@/components/ui/table';
import type { FundMemberFund } from '@/types/fundMember';
import { formatMonthYear } from '@/utils/formatters';
import {
  CarryPositionHeads,
  CarryPositionNameCell,
  CarryPositionShareCells,
  CarryPositionVestingCells,
} from './CarryPositionCells';

export type CarryPosition = Pick<
  FundMemberFund,
  | 'fund_id'
  | 'fund_name'
  | 'vesting_start_date'
  | 'vesting_end_date'
  | 'units'
  | 'total_carry_units'
  | 'vested_units'
  | 'vesting_status'
  | 'vested_days'
  | 'unvested_days'
>;

interface RowLabel {
  name: string;
  sub: string;
}

function periodLabel(position: CarryPosition): string {
  if (position.vesting_status === 'FROZEN') return 'vesting frozen';
  if (!position.vesting_start_date || !position.vesting_end_date) return '';
  return `${formatMonthYear(position.vesting_start_date)} – ${formatMonthYear(position.vesting_end_date)}`;
}

function fundLabel(position: CarryPosition): RowLabel {
  return { name: position.fund_name, sub: periodLabel(position) };
}

interface CarryHoldingPositionsTableProps {
  positions: CarryPosition[];
  rowLabel?: (position: CarryPosition) => RowLabel;
}

export function CarryHoldingPositionsTable({
  positions,
  rowLabel = fundLabel,
}: CarryHoldingPositionsTableProps) {
  return (
    <div className="rounded-lg border">
      <Table>
        <TableHeader>
          <CarryPositionHeads />
        </TableHeader>
        <TableBody>
          {positions.map((position) => (
            <TableRow key={position.fund_id}>
              <CarryPositionNameCell {...rowLabel(position)} />
              <CarryPositionShareCells
                units={position.units}
                totalCarryUnits={position.total_carry_units}
              />
              <CarryPositionVestingCells
                units={position.units}
                vestedUnits={position.vested_units}
                vestingStatus={position.vesting_status}
                vestedDays={position.vested_days}
                unvestedDays={position.unvested_days}
              />
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
